import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// NOTE: withCredentials is intentionally FALSE — the platform's edge proxy
// returns `Access-Control-Allow-Origin: *` alongside `Allow-Credentials: true`,
// which Safari (and other strict browsers) rejects, breaking login on Apple
// devices. We rely exclusively on Bearer-token auth from localStorage instead
// of cookies.
export const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

// Attach Bearer token from localStorage as fallback (covers Safari ITP / cross-domain cookie issues).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-redirect to login on 401 — but ONLY for genuine session-expiry cases.
// Skip endpoints where 401 means something else (e.g. face mismatch on punch,
// auth probes themselves) — otherwise users get force-logged-out by transient
// API errors mid-flow.
const AUTH_FAILURE_SAFE_PATHS = ['/auth/', '/mobile/attendance/punch', '/mobile/attendance/kiosk-identify'];

// Normalize Pydantic v2 validation errors (`detail` is an array of objects
// like `{type, loc, msg, input, url}`) into a single human-readable string.
// Without this, forms that render `err.response.data.detail` directly as a
// React child crash with "Objects are not valid as a React child" (white screen).
const _normalizePydanticDetail = (raw) => {
  if (raw == null) return raw;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((d) => {
        if (!d || typeof d !== 'object') return String(d);
        const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        return field ? `${field}: ${d.msg || 'invalid value'}` : (d.msg || 'Invalid input');
      })
      .join(' · ');
  }
  if (typeof raw === 'object') return raw.msg || raw.detail || JSON.stringify(raw);
  return String(raw);
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Coerce Pydantic validation `detail` (an ARRAY of field errors, or a single
    // `{type, loc, msg, input}` object) into a string BEFORE any page renders it —
    // otherwise React throws "Objects are not valid as a React child".
    // Structured DOMAIN payloads — e.g. a 409 completion-guard {message, redirect_to},
    // a 422 punch result {code, message}, or a 403 RBAC reason — are PRESERVED so
    // downstream handlers can read their fields.
    if (err?.response?.data?.detail !== undefined) {
      const _detail = err.response.data.detail;
      const _isPydantic = Array.isArray(_detail) ||
        (_detail && typeof _detail === 'object' && ('msg' in _detail || 'loc' in _detail));
      if (_isPydantic) {
        err.response.data.detail = _normalizePydanticDetail(_detail);
      }
    }
    if (err?.response?.status === 401) {
      const url = err.config?.url || '';
      const skip = AUTH_FAILURE_SAFE_PATHS.some((p) => url.includes(p));
      if (!skip) {
        localStorage.removeItem('access_token');
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  }
);

export const apiGet = (p, params) => api.get(p, { params }).then(r => r.data);
export const apiPost = (p, body, config) => api.post(p, body, config).then(r => r.data);
export const apiPut = (p, body, config) => api.put(p, body, config).then(r => r.data);
export const apiPatch = (p, body, config) => api.patch(p, body, config).then(r => r.data);
export const apiDel = (p, config) => api.delete(p, config).then(r => r.data);
export const apiUpload = (p, formData) => {
  const token = localStorage.getItem('access_token');
  return api.post(p, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).then(r => r.data);
};

// Authenticated file download (PDF/XLSX): fetches as a blob so the Bearer-token
// interceptor applies, then triggers a browser download.
export const apiDownload = async (p, filename) => {
  const r = await api.get(p, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

// Authenticated POST that returns a file (e.g. Tally XML export). Triggers a download.
export const apiDownloadPost = async (p, filename, body) => {
  const r = await api.post(p, body || {}, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};
